import { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Phone, Clock, MapPin, Battery, Zap, Shield, Award, Star, CheckCircle, MessageCircle, ChevronRight, Menu, X } from 'lucide-react';
import VehicleBatteryFinder from './components/VehicleBatteryFinder';
import Rescue from './components/Rescue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND_LOGO = 'https://customer-assets.emergentagent.com/job_0873b6a5-4361-426a-a8a4-b14247528402/artifacts/wprunhx9_delite_logo_wa_1200x1200.jpeg';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    inquiryType: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919347381519?text=Hello, I need battery service', '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+919347381519';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitMessage('Thank you! Redirecting to WhatsApp...');
      
      // Determine WhatsApp number based on inquiry type
      const whatsappNumber = formData.inquiryType === 'sales' ? '919347381519' : '916304974898';
      
      // Create WhatsApp message
      const message = `New ${formData.inquiryType === 'sales' ? 'Sales' : 'Service'} Inquiry:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nService: ${formData.service}\nMessage: ${formData.message}`;
      
      // Redirect to WhatsApp after 1 second
      setTimeout(() => {
        window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
      }, 1000);
      
      setFormData({ name: '', phone: '', inquiryType: '', service: '', message: '' });
    } catch (error) {
      setSubmitMessage('Something went wrong. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1A1A1A' }}>
      {/* Professional Header */}
      <header className="sticky top-0 z-50 shadow-md" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E0E0E0' }}>
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src={BRAND_LOGO} alt="DELITE" style={{ maxHeight: '65px', width: 'auto', objectFit: 'contain' }} data-testid="header-logo" />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#products" className="text-gray-700 hover:text-gray-900 font-medium transition">Products</a>
              <a href="#services" className="text-gray-700 hover:text-gray-900 font-medium transition">Services</a>
              <a href="#about" className="text-gray-700 hover:text-gray-900 font-medium transition">About Us</a>
              <a href="#contact" className="text-gray-700 hover:text-gray-900 font-medium transition">Contact</a>
              <button
                onClick={handleCallClick}
                className="px-5 py-2.5 text-white rounded font-medium transition" style={{ backgroundColor: '#25D366' }}
                data-testid="call-button"
              >
                Call Now
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E0E0E0' }}>
            <nav className="flex flex-col p-4 space-y-3">
              <a href="#products" className="text-gray-700 hover:text-gray-900 font-medium py-2">Products</a>
              <a href="#services" className="text-gray-700 hover:text-gray-900 font-medium py-2">Services</a>
              <a href="#about" className="text-gray-700 hover:text-gray-900 font-medium py-2">About Us</a>
              <a href="#contact" className="text-gray-700 hover:text-gray-900 font-medium py-2">Contact</a>
              <button
                onClick={handleCallClick}
                className="px-5 py-2.5 text-white rounded font-medium transition" style={{ backgroundColor: '#25D366' }}
              >
                Call Now
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Banner */}
      <section 
        className="relative h-[600px] bg-cover bg-center flex items-center" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(26, 26, 26, 0.85), rgba(26, 26, 26, 0.85)), url('https://images.unsplash.com/photo-1632733711679-529326f6db12?w=1200')`,
        }}
        data-testid="hero-section"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl text-white">
            <div className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-6" style={{ backgroundColor: '#FFFFFF', color: '#1A1A1A' }}>
              35+ Years of Excellence
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
              Reliable Battery Solutions
              <br />
              Since 1990
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              Complete battery solutions for automotive, industrial & residential needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleWhatsAppClick}
                className="px-8 py-4 text-white rounded-lg transition font-semibold text-lg flex items-center justify-center gap-2" style={{ backgroundColor: '#25D366' }}
                data-testid="hero-whatsapp-button"
              >
                <MessageCircle size={20} />
                WhatsApp Us
              </button>
              <button
                onClick={handleCallClick}
                className="px-8 py-4 rounded-lg transition font-semibold text-lg flex items-center justify-center gap-2" style={{ backgroundColor: 'transparent', color: '#FFFFFF', border: '2px solid #FFFFFF' }}
                data-testid="hero-call-button"
              >
                <Phone size={20} />
                +91 93473 81519
              </button>
            </div>
            <div className="mt-8 flex items-center gap-2" style={{ color: '#FFFFFF' }}>
              <Zap size={20} />
              <span className="font-semibold">24/7 Emergency Service Available</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="py-16" style={{ backgroundColor: '#242424' }} data-testid="trust-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center" data-testid="trust-indicator-1">
              <div className="text-5xl font-bold mb-2" style={{ color: '#FFFFFF' }}>35+</div>
              <div className="text-gray-400 font-medium">Years Experience</div>
            </div>
            <div className="text-center" data-testid="trust-indicator-2">
              <div className="text-5xl font-bold mb-2" style={{ color: '#FFFFFF' }}>50K+</div>
              <div className="text-gray-400 font-medium">Happy Customers</div>
            </div>
            <div className="text-center" data-testid="trust-indicator-3">
              <div className="text-5xl font-bold mb-2" style={{ color: '#FFFFFF' }}>100%</div>
              <div className="text-gray-400 font-medium">Genuine Products</div>
            </div>
            <div className="text-center" data-testid="trust-indicator-4">
              <div className="text-5xl font-bold mb-2" style={{ color: '#FFFFFF' }}>24/7</div>
              <div className="text-gray-400 font-medium">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Battery Finder - Dynamic Search */}
      <VehicleBatteryFinder />

      {/* Products Section */}
      <section id="products" className="py-20" style={{ backgroundColor: '#1A1A1A' }} data-testid="services-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4" data-testid="services-title">Our Battery Solutions</h2>
            <p className="text-xl text-gray-400">Complete range for all your power needs</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Car Battery */}
            <div className="group rounded-lg overflow-hidden hover:shadow-xl transition-all" style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }} data-testid="service-car-battery">
              <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2E2E2E, #1A1A1A)' }}>
                <Battery size={64} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">Car Battery</h3>
                <ul className="space-y-2 text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Sale & Installation</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Repair Services</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Free Check-up</span></li>
                </ul>
                <button className="font-semibold flex items-center gap-1 hover:gap-2 transition-all" style={{ color: '#FFFFFF' }}>Learn More <ChevronRight size={16} /></button>
              </div>
            </div>

            {/* Bike Battery */}
            <div className="group rounded-lg overflow-hidden hover:shadow-xl transition-all" style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }} data-testid="service-bike-battery">
              <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2E2E2E, #1A1A1A)' }}>
                <Battery size={64} style={{ color: '#25D366' }} />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">Bike Battery</h3>
                <ul className="space-y-2 text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Sale & Installation</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Repair Services</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Quick Service</span></li>
                </ul>
                <button className="font-semibold flex items-center gap-1 hover:gap-2 transition-all" style={{ color: '#FFFFFF' }}>Learn More <ChevronRight size={16} /></button>
              </div>
            </div>

            {/* UPS Battery */}
            <div className="group rounded-lg overflow-hidden hover:shadow-xl transition-all" style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }} data-testid="service-ups-battery">
              <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2E2E2E, #1A1A1A)' }}>
                <Battery size={64} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">UPS Battery</h3>
                <ul className="space-y-2 text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Sale & Installation</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Repair Services</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Office Solutions</span></li>
                </ul>
                <button className="font-semibold flex items-center gap-1 hover:gap-2 transition-all" style={{ color: '#FFFFFF' }}>Learn More <ChevronRight size={16} /></button>
              </div>
            </div>

            {/* Inverter Battery */}
            <div className="group rounded-lg overflow-hidden hover:shadow-xl transition-all" style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }} data-testid="service-inverter-battery">
              <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2E2E2E, #1A1A1A)' }}>
                <Battery size={64} style={{ color: '#25D366' }} />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">Inverter Battery</h3>
                <ul className="space-y-2 text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Sale & Installation</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Repair Services</span></li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} style={{ color: '#25D366' }} /><span>Power Backup</span></li>
                </ul>
                <button className="font-semibold flex items-center gap-1 hover:gap-2 transition-all" style={{ color: '#FFFFFF' }}>Learn More <ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Gallery */}
      <section className="py-20" style={{ backgroundColor: '#242424' }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Professional Service</h2>
            <p className="text-xl text-gray-400">Expert technicians, quality workmanship</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="https://images.unsplash.com/photo-1693718641851-fd3ac0b7af4e?w=600" alt="Battery Service" className="w-full h-64 object-cover hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?w=600" alt="Professional Installation" className="w-full h-64 object-cover hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="https://images.unsplash.com/photo-1676337167752-2062c6ca7366?w=600" alt="Expert Technician" className="w-full h-64 object-cover hover:scale-110 transition-transform duration-500" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="about" className="py-20" style={{ backgroundColor: '#1A1A1A' }} data-testid="why-choose-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4" data-testid="why-choose-title">Why Choose DELITE</h2>
            <p className="text-xl text-gray-400">Your trusted battery partner</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6" data-testid="why-choose-1">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#2E2E2E' }}>
                <Award size={40} style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">35 Years Experience</h3>
              <p className="text-gray-400">Trusted service since 1990 with genuine products and expert solutions</p>
            </div>

            <div className="text-center p-6" data-testid="why-choose-2">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#2E2E2E' }}>
                <Shield size={40} style={{ color: '#25D366' }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">100% Genuine</h3>
              <p className="text-gray-400">Only authentic, branded batteries with proper warranty and certification</p>
            </div>

            <div className="text-center p-6" data-testid="why-choose-3">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#2E2E2E' }}>
                <Zap size={40} style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">24/7 Emergency</h3>
              <p className="text-gray-400">Round-the-clock emergency service for your peace of mind</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 text-white" style={{ backgroundColor: '#242424' }} data-testid="contact-section">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-4xl font-bold mb-8" data-testid="contact-title">Get In Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-info-address">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#3A3A3A' }}>
                    <MapPin size={24} style={{ color: '#FFFFFF' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Visit Us</h3>
                    <p className="text-gray-400">
                      27-20-7, Museum Road<br />
                      Vijayawada, 520010<br />
                      Andhra Pradesh
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-info-phone">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#3A3A3A' }}>
                    <Phone size={24} style={{ color: '#25D366' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Call Us</h3>
                    <a href="tel:+919347381519" className="text-2xl font-bold transition" style={{ color: '#25D366' }}>
                      +91 93473 81519
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-info-hours">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#3A3A3A' }}>
                    <Clock size={24} style={{ color: '#FFFFFF' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Working Hours</h3>
                    <p className="text-gray-400">
                      Regular: 10:00 AM - 9:00 PM<br />
                      Emergency: 24/7 Available
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleWhatsAppClick}
                  className="mt-6 px-8 py-4 text-white rounded-lg transition font-semibold flex items-center gap-2" style={{ backgroundColor: '#25D366' }}
                >
                  <MessageCircle size={20} />
                  Message on WhatsApp
                </button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-lg p-8" style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }} data-testid="contact-form">
              <h3 className="text-2xl font-bold text-white mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Inquiry Type *</label>
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white" style={{ backgroundColor: '#3A3A3A', border: '1px solid #4A4A4A' }}
                    data-testid="contact-form-inquiry-type"
                  >
                    <option value="">Select Inquiry Type</option>
                    <option value="sales">Sales Inquiry</option>
                    <option value="service">Service Request</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-500" style={{ backgroundColor: '#3A3A3A', border: '1px solid #4A4A4A' }}
                    placeholder="Enter your name"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-500" style={{ backgroundColor: '#3A3A3A', border: '1px solid #4A4A4A' }}
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Service Needed *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white" style={{ backgroundColor: '#3A3A3A', border: '1px solid #4A4A4A' }}
                    data-testid="contact-form-service"
                  >
                    <option value="">Select Service</option>
                    <option value="car-battery">Car Battery</option>
                    <option value="bike-battery">Bike Battery</option>
                    <option value="ups-battery">UPS Battery</option>
                    <option value="inverter-battery">Inverter Battery</option>
                    <option value="emergency">Emergency Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-500" style={{ backgroundColor: '#3A3A3A', border: '1px solid #4A4A4A' }}
                    placeholder="Tell us about your battery needs..."
                    data-testid="contact-form-message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 text-white rounded-lg transition font-bold text-lg disabled:bg-gray-600" style={{ backgroundColor: '#25D366' }}
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Request'}
                </button>

                {submitMessage && (
                  <p className={`text-center font-semibold ${submitMessage.includes('Thank you') ? 'text-green-600' : 'text-red-600'}`} data-testid="contact-form-message-status">
                    {submitMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-gray-400 py-12" style={{ backgroundColor: '#111111', borderTop: '1px solid #3A3A3A' }} data-testid="footer">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img src={BRAND_LOGO} alt="DELITE" className="h-12 object-contain" data-testid="footer-logo" />
              </div>
              <p className="text-sm">Your trusted battery experts since 1990</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">Car Battery</a></li>
                <li><a href="#" className="hover:text-white transition">Bike Battery</a></li>
                <li><a href="#" className="hover:text-white transition">UPS Battery</a></li>
                <li><a href="#" className="hover:text-white transition">Inverter Battery</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="hover:text-white transition">About Us</a></li>
                <li><a href="#services" className="hover:text-white transition">Services</a></li>
                <li><a href="#contact" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>27-20-7, Museum Road</li>
                <li>Vijayawada, 520010</li>
                <li>Andhra Pradesh</li>
                <li className="pt-2">
                  <a href="tel:+919347381519" className="font-semibold" style={{ color: '#25D366' }}>+91 93473 81519</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 text-center text-sm" style={{ borderTop: '1px solid #3A3A3A' }}>
            <p>&copy; 2025 Delite Autolec Company. All Rights Reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Chat Widget loaded via AiSensy script in index.html */}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/rescue" element={<Rescue />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;